import { inject, Injectable } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ActionWorkflowNode, DesignFilesAction, MessageType, TreatmentFileKind } from 'app/core/enums';
import { findTreatmentTypeImage } from 'app/core/constants';
import { TreatmentApiClient, TreatmentDesignActionApiClient, TreatmentTypeApiClient } from 'app/core/api-clients';
import { FileInfo, InProgressTreatment, Message, RevisionReason } from 'app/core/models';
import { LoadHelper } from './load-helper.service';
import { NavigationService } from './navigation.service';
import { ChatMessageService } from './chat-message.service';
import { RequestRevisionModalService } from './modals/request-revision-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { PatientTreatment } from 'app/core/models/patient/patient-treatment.model';

type OrderQuickActionSource = InProgressTreatment | PatientTreatment;

interface OrderQuickActionTarget {
  treatmentId: string;
  patientId: string;
  treatmentTypeId: number;
}

export interface OrderQuickActionRedesignOptions {
  onSuccess?: () => void | Promise<void>;
}

@Injectable()
export class OrderQuickActionsService {
  private readonly _loadHelper = inject(LoadHelper);
  private readonly _treatmentClient = inject(TreatmentApiClient);
  private readonly _treatmentTypeClient = inject(TreatmentTypeApiClient);
  private readonly _messageClient = inject(ChatMessageService);
  private readonly _designActionClient = inject(TreatmentDesignActionApiClient);
  private readonly _requestRevisionModalService = inject(RequestRevisionModalService);
  private readonly _translateService = inject(TranslateService);
  private readonly _navigationService = inject(NavigationService);
  private readonly _confirmationService = inject(ConfirmationService);
  private readonly _isRequestingRevision$ = new BehaviorSubject<boolean>(false);

  readonly isRequestingRevision$ = this._isRequestingRevision$.asObservable();

  async reprint(source: OrderQuickActionSource): Promise<void> {
    await this.navigateWithAction(this.toTarget(source), ActionWorkflowNode.Reprint);
  }

  async upgradeToVeneers(source: OrderQuickActionSource): Promise<void> {
    await this.navigateWithAction(this.toTarget(source), ActionWorkflowNode.UpgradeToVeneers);
  }

  async redesign(source: OrderQuickActionSource, options?: OrderQuickActionRedesignOptions): Promise<void> {
    if (!source?.id) return;

    try {
      const order = await firstValueFrom(
        this._loadHelper.loading(this._treatmentClient.getInProgressTreatment(source.id))
      );

      const conversationId = order?.conversation?.id;
      if (!conversationId) {
        this.showRedesignError('No conversation found for this order');
        return;
      }

      let messages: Message[] = [];
      let files: FileInfo[] = [];
      let revisionReasons: RevisionReason[] = [];

      try {
        const [msgs, designFiles, reasons] = await Promise.all([
          firstValueFrom(this._messageClient.observeMessages(conversationId)),
          firstValueFrom(
            this._loadHelper.loading(this._treatmentClient.getKindFiles(order.id, TreatmentFileKind.Design))
          ),
          firstValueFrom(this._loadHelper.loading(this._treatmentTypeClient.getTypeReasons(order.treatmentTypeId))),
        ]);

        messages = msgs ?? [];
        files = designFiles ?? [];
        revisionReasons = (reasons as RevisionReason[]) ?? [];
      } catch (err) {
        console.error('Error fetching redesign prerequisites', err);
        this.showRedesignError('Failed to load necessary data for redesign');
        return;
      }

      const messageWithFiles = [...messages].find(m => m.hasFiles);
      if (!messageWithFiles || !files || files.length === 0) {
        this.showRedesignError('No design files found for this order');
        return;
      }

      const treatmentTypeImage = findTreatmentTypeImage(order.treatmentTypeId);
      const treatmentTypeName = treatmentTypeImage?.name || order?.treatmentType;
      const headerMessage = this._translateService.instant('messages.redesignAndRemanufactureMessage');

      const modalResult = await this._requestRevisionModalService.open({
        data: {
          reasons: revisionReasons,
          patientName: order?.patient,
          treatmentType: treatmentTypeName,
          headerMessage,
          requestRevisionFn: async (reason, comment) => {
            this._isRequestingRevision$.next(true);
            try {
              const actionMessageId = await firstValueFrom(
                this._messageClient.createNewMessage(
                  MessageType.RequestRevision,
                  conversationId,
                  encodeURI(comment),
                  [],
                  order.id,
                  false
                )
              );

              if (actionMessageId == null) throw new Error('Failed to create action message');

              return await firstValueFrom(
                this._designActionClient.requestAction({
                  actionMessageId,
                  sourceMessageId: messageWithFiles.id,
                  action: DesignFilesAction.Revision,
                  revisionReasonId: reason.id,
                  treatmentId: order.id,
                  preAuthorizedCharges: null,
                  skipTreatmentInfoMessage: true,
                  isRedesign: true,
                })
              )
                .then(
                  () => true,
                  () => false
                )
                .finally(() => {});
            } catch (err) {
              console.error('requestRevision failed', err);
              this.showRedesignError('An error occurred while requesting redesign');
              return false;
            } finally {
              this._isRequestingRevision$.next(false);
            }
          },
          isLoading$: this.isRequestingRevision$,
        },
      });

      if (modalResult) {
        // The revision is already committed at this point, so nothing below may reach the catch and
        // report it as a failure - that would invite the user to submit a duplicate revision.
        // onSuccess can stay pending forever (see OrderDetailStore.refreshUnsentFiles) and the
        // navigation rejects on a stale lazy chunk or a guard error, so both are isolated.
        void Promise.resolve(options?.onSuccess?.()).catch(err => console.error('Redesign onSuccess failed', err));
        await this._navigationService
          .navigateToOrderDetail(order.id)
          .catch(err => console.error('Redesign navigation failed', err));
      }
    } catch (error) {
      console.error('Error opening redesign and remanufacture modal:', error);
      this.showRedesignError('An unexpected error occurred');
    }
  }

  private toTarget(source: OrderQuickActionSource): OrderQuickActionTarget {
    return {
      treatmentId: source.id,
      patientId: source.patientId,
      treatmentTypeId: source.treatmentTypeId,
    };
  }

  private async navigateWithAction(target: OrderQuickActionTarget, action: ActionWorkflowNode): Promise<void> {
    await this._navigationService.navigateToSubmitOrder({
      patientId: target.patientId,
      id: target.treatmentId,
      treatmentType: target.treatmentTypeId,
      action,
    });
  }

  private showRedesignError(message: string): void {
    this._confirmationService.confirm({
      header: 'Unable to request Redesign',
      message,
      closable: true,
      acceptButtonProps: { label: 'OK', severity: 'secondary' },
    });
  }
}
